// "Is the user in the middle of something?" - decides whether a branding
// change made by the Super Admin is applied right away or waits.
//
// Busy means: they have typed into a form that has not been submitted (or
// saved, or navigated away from) yet, or a dialog is open. Merely having a
// field focused is not busy - the login page focuses its email box itself.

let dirty = false;

const NON_EDITING_INPUT_TYPES = new Set(["button", "submit", "reset", "image", "hidden"]);

function isEditable(el: EventTarget | null): boolean {
  if (!(el instanceof HTMLElement)) return false;
  if (el instanceof HTMLInputElement) return !NON_EDITING_INPUT_TYPES.has(el.type) && !el.readOnly && !el.disabled;
  if (el instanceof HTMLTextAreaElement) return !el.readOnly && !el.disabled;
  if (el instanceof HTMLSelectElement) return !el.disabled;
  return el.isContentEditable;
}

/** Starts watching for typing/submits. Returns the cleanup. */
export function startActivityTracking(doc: Document = document): () => void {
  const onEdit = (e: Event) => {
    if (isEditable(e.target)) dirty = true;
  };
  const onSubmit = () => {
    dirty = false;
  };
  doc.addEventListener("input", onEdit, true);
  doc.addEventListener("change", onEdit, true);
  doc.addEventListener("submit", onSubmit, true);
  return () => {
    doc.removeEventListener("input", onEdit, true);
    doc.removeEventListener("change", onEdit, true);
    doc.removeEventListener("submit", onSubmit, true);
  };
}

/** The current piece of work is finished (saved, or the user moved on). */
export function markWorkFinished() {
  dirty = false;
}

export function isUserBusy(doc: Document = document): boolean {
  if (dirty) return true;
  const dialogs = doc.querySelectorAll<HTMLElement>('[role="dialog"], [role="alertdialog"], dialog[open]');
  return Array.from(dialogs).some((d) => !d.closest('[hidden], [aria-hidden="true"]'));
}
